import React, { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import styled from "styled-components";
import tw from "twin.macro";
import { css } from "styled-components/macro"; //eslint-disable-line
import { ReactComponent as QuotesLeftIcon } from "../../images/quotes-l.svg";
import { ReactComponent as QuotesRightIcon } from "../../images/quotes-r.svg";
import {
  IconCircleChevronRightFilled,
  IconCircleChevronLeftFilled,
} from "@tabler/icons-react";
import useGetSliders from "../../data/useGetSliders";

import img2330 from "../../images/IMG_2330.png";
import { getAbsoluteUrl } from "utils/urlUtils";
const addWordSpacing = (text) => {
  if (!text || typeof text !== "string") return null;
  return text.split(" ").map((word, idx) => (
    <span key={idx} style={{ wordSpacing: "0.4rem" }}>
      {word}{" "}
    </span>
  ));
};

const Container = tw.div`relative`;
const Content = tw.div`w-full py-5 lg:py-5`;
const LoadingContainer = tw.div`w-full py-20 flex items-center justify-center`;
const LoadingText = tw.p`text-lg text-gray-600`;
const ErrorContainer = tw.div`w-full py-20 flex items-center justify-center bg-red-100`;
const ErrorText = tw.p`text-lg text-red-600`;

const TestimonialSliderContainer = tw.div`mt-24`;
const TestimonialSlider = styled(Slider)`
  .slick-list {
    overflow: hidden;
  }
  .slick-track {
    display: flex;
    align-items: stretch;
  }
  .slick-slide {
    outline: none;
  }
  .slick-slide > div {
    height: 100%;
  }
  .slick-dots {
    margin-top: 1rem;
  }
  .slick-dots li button:before {
    color: #c78a41; /* brand primary */
    opacity: 0.5;
  }
  .slick-dots li.slick-active button:before {
    color: #c78a41;
    opacity: 1;
  }
`;
const Testimonial = tw.div`flex! flex-col items-center md:items-stretch md:flex-row md:justify-center outline-none`;
const ImageContainer = styled.div`
  ${tw`mx-auto md:mx-3 lg:mx-6 w-full md:w-4/12 rounded flex items-center max-w-xs md:max-w-none`}
  img {
    ${tw`rounded shadow-xl`}
    transition: transform 0.3s ease-in-out;
    object-fit: cover;
    height: 400px;
    width: 100%;

    @media (max-width: 768px) {
      height: 300px;
    }
  }
`;
const TextContainer = tw.div`md:mx-3 lg:mx-6 md:w-6/12 py-4 flex flex-col justify-between`;
const QuoteContainer = tw.div`relative p-6 md:p-8 lg:p-10 mt-4 md:mt-0`;
const Quote = tw.blockquote`text-right md:text-right font-medium text-xl lg:text-2xl xl:text-3xl [font-family: 'CustomFont']`;
const CustomerInfo = tw.div`px-5 lg:px-10 text-center md:text-left mt-4 md:mt-0`;
const CustomerName = tw.h5`
  font-bold text-lg lg:text-xl xl:text-2xl text-primary-500 
  whitespace-pre-line tracking-normal
`;

const CustomerTitle = tw.p`font-medium text-sm`;

const QuotesLeft = tw(
  QuotesLeftIcon,
)`w-8 h-8 lg:w-10 lg:h-10 text-primary-500 absolute top-0 left-0`;
const QuotesRight = tw(
  QuotesRightIcon,
)`w-8 h-8 lg:w-10 lg:h-10 text-primary-500 absolute bottom-0 right-0`;

const SliderControlButtonContainer = styled.div`
  ${tw`absolute top-0 h-full flex items-end md:items-center z-20`}
  button {
    ${tw`text-secondary-500 hover:text-primary-600 focus:outline-none transition duration-300 hover:scale-125 -translate-y-2/3 md:translate-y-0`}
    svg {
      ${tw`w-8`}
    }
  }
`;

const NextArrow = React.forwardRef(
  ({ currentSlide, slideCount, ...props }, ref) => (
    <SliderControlButtonContainer tw="right-0">
      <button ref={ref} {...props}>
        <IconCircleChevronRightFilled size={48} />
      </button>
    </SliderControlButtonContainer>
  ),
);
const PreviousArrow = React.forwardRef(
  ({ currentSlide, slideCount, ...props }, ref) => (
    <SliderControlButtonContainer tw="left-0">
      <button ref={ref} {...props}>
        <IconCircleChevronLeftFilled size={48} />
      </button>
    </SliderControlButtonContainer>
  ),
);

export default () => {
  const sliderLimit = parseInt(process.env.REACT_APP_SLIDERS_LIMIT || "5", 10);
  const { data: sliders = [], isLoading, isError } = useGetSliders(sliderLimit);

  // Fallback testimonials if data is not available
  const fallbackTestimonials = [
    {
      imageSrc: img2330,
      quote:
        "جامعہ محمدیہ عربیہ، رائیدرگ میں شعبۂ ثقافت کے زیرِ اہتمام 12 شعبان 1446ھ مطابق 11 فروری 2025ء، بروز منگل ایک عظیم الشان علمی نشست برائے طلبۂ عالمیت و فضیلت منعقد ہوئی۔اس  محاضرہ میں فضیلتہ الشیخ محمد معاذ ابوقحافہ عمری ندوی حفظہ اللہ (استاذِ حدیث) نے طلبہ سے خطاب فرمایا۔شیخ محترم نے “جمع و تدوینِ حدیث میں محدثین کی محنت و صبر” اور “مساجد و مدارس کو میدانِ عمل بنانے میں محدثین کا اسوہ” جیسے دو نہایت اہم اور علمی موضوعات پر روشنی ڈالی۔یہ دونوں وقیع محاضرات صبح 9 بجے تا عصر، محمدیہ جنرل لائبریری میں منعقد ہوئے، جن میں طلبہ نے کثیر تعداد میں شرکت کی اور شیخ محترم کے علمی نکات سے استفادہ کیا۔",
      customerName: "Jamia Mohammadia Arabia",
      customerTitle: "Rayadurg, Andhra Paradesh.",
    },
    {
      imageSrc: process.env.PUBLIC_URL + "/logo512.png",
      quote:
        "	طلبہ کی اخلاقی و دینی تربیت کو اولین ترجیح دینا- دینی تعلیم کے ساتھ عصری علوم کی تدریس پر بھی خصوصی توجہ دینا۔",
      customerName: "Jamia Mohammadia Arabia",
      customerTitle: "Rayadurg, Andhra Paradesh.",
    },
    {
      imageSrc: process.env.PUBLIC_URL + "/logo512.png",
      quote:
        "جدید ٹیکنالوجی کے ذریعے تعلیمی ترقی، مقابلہ جاتی سرگرمیوں اور ثقافتی پروگراموں کو فروغ دینا۔  مختلف قومی اور بین الاقوامی جامعات سے الحاق کے ذریعے تعلیمی معیار کو بلند کرنا",
      customerName: "Jamia Mohammadia Arabia",
      customerTitle: "Rayadurg, Andhra Paradesh.",
    },
  ];

  // Use sliders from API or fallback to default testimonials
  const testimonials = sliders.length > 0 ? sliders : fallbackTestimonials;

  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingText>Loading sliders...</LoadingText>
        </LoadingContainer>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container>
        <ErrorContainer>
          <ErrorText>
            Failed to load sliders. Showing default content.
          </ErrorText>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <TestimonialSliderContainer>
          <TestimonialSlider
            autoplay
            autoplaySpeed={5000}
            pauseOnHover
            infinite
            speed={800}
            slidesToShow={1}
            slidesToScroll={1}
            fade
            nextArrow={<NextArrow />}
            prevArrow={<PreviousArrow />}
          >
            {testimonials.map((testimonial, index) => (
              <Testimonial key={index}>
                <ImageContainer>
                  <img
                    src={getAbsoluteUrl(testimonial.imageUrl)}
                    alt={testimonial.customerName}
                  />
                </ImageContainer>
                <TextContainer>
                  <QuoteContainer>
                    <QuotesLeft />
                    <Quote>{testimonial.description}</Quote>
                    <QuotesRight />
                  </QuoteContainer>
                  <CustomerInfo>
                    <CustomerName>
                      {addWordSpacing(testimonial.name)}
                    </CustomerName>
                    <CustomerTitle>
                      {addWordSpacing("Rayadurg, Andhra Pradesh")}
                    </CustomerTitle>
                  </CustomerInfo>
                </TextContainer>
              </Testimonial>
            ))}
          </TestimonialSlider>
        </TestimonialSliderContainer>
      </Content>
    </Container>
  );
};
